// Send + keyboard-shortcut handling for the chat composer.
// The send routine wires the streamChat() async iterator into chatStore
// actions. Provided as a hook so ChatPanel can pass the returned callbacks
// straight to the composer + textarea.
import { useCallback, type KeyboardEvent } from 'react';
import { report } from '@/core/rendererErrorReporter';
import { chatStore } from '@/lib/chatStore';
import { streamChat } from '@/lib/chatStream';
import { useTranscription } from '@/lib/useTranscription';

interface UseChatSendOptions {
  isStreaming: boolean;
  transcription: ReturnType<typeof useTranscription>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

// After this long with NO stream activity at all — not even a server `: ping`
// heartbeat (15s) or a reconnect attempt — show a soft, non-fatal note. It must
// NEVER abort: the turn runs durably server-side and streamChat keeps re-attaching
// (`?since=`). Aborting here used to kill that recovery, stranding a finished turn
// behind a false "backend may be stuck" error — the mobile sleep/wake bug.
const STALL_NOTICE_MS = 5 * 60 * 1000;

export function useChatSend({ isStreaming, transcription, textareaRef }: UseChatSendOptions) {
  const send = useCallback(async (): Promise<void> => {
    if (chatStore.getState().isStreaming) return;
    // Stop any in-flight voice capture before send — avoids the recording
    // continuing in the background after the message goes out.
    if (transcription.state === 'recording') transcription.stop();

    const trimmed = chatStore.getInput().trim();
    const currentAttachments = [...chatStore.getState().pendingAttachments];
    if (trimmed.length === 0 && currentAttachments.length === 0) return;

    // The bubble is what the server stores: the typed text and the attachment
    // records. What the model is told about the files is the server's to build.
    const { userId, assistantId } = chatStore.appendUserAndAssistant(
      trimmed,
      currentAttachments.length > 0 ? currentAttachments : undefined,
    );
    chatStore.setInput('');
    chatStore.clearPendingAttachments();
    // A new message supersedes any running turn server-side, which cancels a
    // pending ask_operator question — but its question_resolved event lands in
    // the OLD turn's buffer, invisible to this client's new stream. Clear the
    // card locally so a stale question can't sit over the new turn.
    chatStore.clearPendingQuestion();
    chatStore.setStreaming(true);

    const controller = chatStore.createAbortController();
    const state = chatStore.getState();

    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let stallNotified = false;
    const resetStallTimer = (): void => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        if (stallNotified) return;
        stallNotified = true;
        // Non-fatal, and NOT an abort: the turn keeps running on the desktop and
        // the stream keeps trying to re-attach. We only note the prolonged silence
        // so the user isn't staring at a dead-looking spinner.
        chatStore.addSystemNotice(
          'Still working on it — this turn keeps running on the desktop and will be saved even if you close the app.',
        );
      }, STALL_NOTICE_MS);
    };

    // Set by the first event: the request was accepted and the server has
    // stored the turn, so a later failure must not take it off the screen.
    let streamStarted = false;
    try {
      resetStallTimer();
      for await (const event of streamChat({
        message: trimmed,
        ...(state.conversationId ? { sessionId: state.conversationId } : {}),
        // Provider/model travel ONLY on a deliberate picker selection — the
        // backend owns routing truth (pinned conversation > channel config).
        // Sending hydrated display state as an override forced legacy
        // 'anthropic' metas onto the SDK path mid-CLI-conversation. The
        // extended-context tier rides alongside an explicit selection.
        ...(state.explicitSelection
          ? { provider: state.provider, model: state.model, useExtendedContext: state.useExtendedContext }
          : {}),
        // Uploaded files, by the ids the upload returned — the server finds them.
        ...(currentAttachments.length > 0 ? { attachmentIds: currentAttachments.map((a) => a.id) } : {}),
        signal: controller.signal,
      })) {
        if (controller.signal.aborted) break;
        streamStarted = true;
        resetStallTimer();

        // Content events (text, tool calls, sub-agent progress) re-render on a
        // throttle — a reply can deliver hundreds in one network chunk. Every other
        // event notifies at once, flushing any content still waiting with it.
        if (event.kind === 'route') {
          // Backend routing truth — selector reflects what actually runs, and
          // the reason drives the deviation notice (budget / failover / re-pin /
          // extended-context downgrade on transports that can't serve 1M).
          chatStore.applyRoute(event.provider, event.model, event.useExtendedContext, event.reason, event.effort);
        } else if (event.kind === 'turn_meta') {
          // Injection metadata — what was injected into the agent's context.
          chatStore.setTurnMeta({
            executor: event.executor,
            systemPrompt: event.systemPrompt,
            budget: event.budget,
            context: event.context,
          });
        } else if (event.kind === 'text') {
          chatStore.withThrottledNotify(() => chatStore.appendTextToAssistant(assistantId, event.content));
        } else if (event.kind === 'tool_call') {
          chatStore.withThrottledNotify(() =>
            chatStore.appendToolCallToAssistant(assistantId, {
              toolId: event.toolId,
              name: event.name,
              input: event.input,
              startedAt: event.startedAt,
            }),
          );
        } else if (event.kind === 'tool_call_input') {
          chatStore.withThrottledNotify(() =>
            chatStore.updateToolCallInput(assistantId, {
              toolId: event.toolId,
              input: event.input,
            }),
          );
        } else if (event.kind === 'tool_result') {
          chatStore.withThrottledNotify(() =>
            chatStore.appendToolResultToAssistant(assistantId, {
              toolId: event.toolId,
              output: event.output,
              durationMs: event.durationMs,
              isError: event.isError,
              ...(event.isWarning ? { isWarning: event.isWarning } : {}),
            }),
          );
        } else if (event.kind === 'turn_complete') {
          // Single end-of-turn event — updates cost, context meter, and usage in one action.
          chatStore.applyTurnComplete(event);
        } else if (event.kind === 'subagent') {
          chatStore.withThrottledNotify(() =>
            chatStore.updateSubAgentProgress(assistantId, {
              subType: event.subType,
              ...(event.name !== undefined ? { name: event.name } : {}),
              ...(event.id !== undefined ? { id: event.id } : {}),
              ...(event.partialJson !== undefined ? { partialJson: event.partialJson } : {}),
              ...(event.result !== undefined ? { result: event.result } : {}),
              ...(event.isError !== undefined ? { isError: event.isError } : {}),
              ...(event.text !== undefined ? { text: event.text } : {}),
            }),
          );
        } else if (event.kind === 'question') {
          // Mid-turn operator question (ask_operator) — render the card.
          chatStore.setPendingQuestion({
            id: event.id,
            question: event.question,
            ...(event.header !== undefined ? { header: event.header } : {}),
            questionKind: event.questionKind,
            options: event.options,
            timeoutMs: event.timeoutMs,
            askedAt: event.askedAt,
          });
        } else if (event.kind === 'question_resolved') {
          // Settled server-side (answer, decline, timeout, stop) — drop the card.
          chatStore.clearPendingQuestion(event.id);
        } else if (event.kind === 'system') {
          chatStore.addSystemNotice(event.content);
        } else if (event.kind === 'error') {
          // Server-side error — user message IS persisted but the assistant
          // placeholder may be empty. Remove the "Thinking…" ghost if no
          // content arrived; keep it if partial output was received.
          chatStore.removeEmptyAssistant(assistantId);
          chatStore.addError(event.message);
        } else if (event.kind === 'reconnecting') {
          // Transparent: the turn keeps running server-side and we're re-attaching
          // to it; text resumes on its own. No error UI for an expected blip.
        } else if (event.kind === 'interrupted') {
          chatStore.removeEmptyAssistant(assistantId);
          chatStore.addError(
            'Connection lost. The turn kept running on the desktop — reopen this conversation to see the result.',
          );
        }
      }
      if (controller.signal.aborted) {
        chatStore.appendTextToAssistant(assistantId, '\n\n_(stopped)_');
      }
    } catch (err) {
      if (controller.signal.aborted) {
        chatStore.appendTextToAssistant(assistantId, '\n\n_(stopped)_');
      } else if (streamStarted) {
        // Applying the reply threw — a dashboard bug, not a failed request
        // (streamChat handles network drops itself). The turn is stored and may
        // still be running on the desktop, so keep it, say where the result is,
        // and report the bug: this catch is the only place it surfaces.
        report({ source: 'chat-stream', error: err });
        chatStore.removeEmptyAssistant(assistantId);
        chatStore.addError(
          'Showing the reply failed. The turn kept running on the desktop — reopen this conversation to see the result.',
        );
      } else {
        // The request failed before any reply — a non-2xx answer or no answer.
        // The server never persisted the user message (early returns in chat.ts
        // happen before appendMessage). Remove both placeholder messages and
        // restore the user's input so they can retry.
        chatStore.removeFailedTurn(userId, assistantId);
        chatStore.setInput(trimmed);
        if (currentAttachments.length > 0) {
          for (const att of currentAttachments) {
            chatStore.addPendingAttachment(att);
          }
        }
        chatStore.addError(err instanceof Error ? err.message : 'Stream failed');
      }
    } finally {
      if (stallTimer) clearTimeout(stallTimer);
      // Only clear streaming state if this turn is still the active one — a
      // conversation switch may have aborted us and started a newer stream.
      if (chatStore.getState().abortController === controller) {
        chatStore.setStreamingDone();
      }
      textareaRef.current?.focus();
    }
  }, [transcription, textareaRef]);

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Escape' && isStreaming) {
        e.preventDefault();
        chatStore.stopStreaming();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void send();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void send();
      }
    },
    [isStreaming, send],
  );

  return { send, handleKey };
}
